import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * In-game achievements that unlock cosmetics. Earned from gameplay on every platform and persisted
 * locally (PlayerPreferences), so rewards are available offline after being earned. On Android/iOS
 * the Lucky unlock is also mirrored to the OS games service via [reportLuckyAchievementToPlatform];
 * that mirror never gates the skin.
 */
object Achievements {
    private var prefs: PlayerPreferences? = null
    private val mutableLuckyDiceUnlocked = MutableStateFlow(false)
    val diceUnlocked: StateFlow<Boolean> = mutableLuckyDiceUnlocked.asStateFlow()
    private val mutableCreatorDiceUnlocked = MutableStateFlow(false)
    val creatorDiceUnlocked: StateFlow<Boolean> = mutableCreatorDiceUnlocked.asStateFlow()
    private val mutablePracticeDiceUnlocked = MutableStateFlow(false)
    val practiceDiceUnlocked: StateFlow<Boolean> = mutablePracticeDiceUnlocked.asStateFlow()

    /** Seed unlock state from persistence, then reconcile with the OS games service. Call once at startup. */
    fun init(playerPreferences: PlayerPreferences) {
        prefs = playerPreferences
        mutableLuckyDiceUnlocked.value = playerPreferences.getAchievementDiceUnlocked()
        mutableCreatorDiceUnlocked.value = playerPreferences.getCreatorDiceUnlocked()
        mutablePracticeDiceUnlocked.value = playerPreferences.getPracticeDiceUnlocked()
        // Recover an unlock earned on another device/install from the OS games service (best-effort,
        // async). No-op on platforms without a games-service mirror.
        reconcileLuckyAchievementFromPlatform()
    }

    /** Earn the "Lucky" achievement (rolled a 7 three times in a row). Returns true on first unlock. */
    fun unlockLuckyDice(): Boolean {
        if (mutableLuckyDiceUnlocked.value) return false
        applyLuckyUnlock()
        // Mirror to the OS games service where configured (best-effort); never gates the skin.
        reportLuckyAchievementToPlatform()
        return true
    }

    /** Earn the creator encounter dice. Returns true on first unlock. */
    fun unlockCreatorDice(): Boolean {
        if (mutableCreatorDiceUnlocked.value) return false
        mutableCreatorDiceUnlocked.value = true
        prefs?.setCreatorDiceUnlocked(true)
        return true
    }

    /** Earn the Training Wheels dice (finished a practice round). Returns true on first unlock. */
    fun unlockPracticeDice(): Boolean {
        if (mutablePracticeDiceUnlocked.value) return false
        mutablePracticeDiceUnlocked.value = true
        prefs?.setPracticeDiceUnlocked(true)
        return true
    }

    /**
     * Called by the platform when it discovers the achievement was already earned elsewhere (e.g.
     * Play Games on a new phone). Persists locally but does NOT push back to the platform.
     */
    internal fun applyLuckyUnlockFromPlatform() {
        if (mutableLuckyDiceUnlocked.value) return
        applyLuckyUnlock()
    }

    private fun applyLuckyUnlock() {
        mutableLuckyDiceUnlocked.value = true
        prefs?.setAchievementDiceUnlocked(true)
    }
}

/**
 * Marks whether the LOCAL game currently in play was launched as a practice round (via the
 * first-launch offer card or the Training Wheels unlock dialog). The game screen consumes the
 * marker when it opens — so it can never leak into a later single-device game — and checks it at
 * game-over to award the Training Wheels dice. Runtime only — a practice game abandoned to
 * process death just has to be replayed.
 */
object PracticeGameSession {
    var isPracticeGame = false
        private set

    // One-shot: after a completed practice round, the start screen opens the dice picker so the
    // freshly earned Training Wheels are the first thing the returning player sees.
    private var diceShowcaseRequested = false

    fun markLaunched() {
        isPracticeGame = true
    }

    /** Claims the launch marker for the game screen that's opening. */
    fun consumeLaunch(): Boolean = isPracticeGame.also { isPracticeGame = false }

    fun requestDiceShowcase() {
        diceShowcaseRequested = true
    }

    fun consumeDiceShowcase(): Boolean = diceShowcaseRequested.also { diceShowcaseRequested = false }
}

/**
 * One-time teaching hints, persisted so each fires exactly once per install. Delivered in context
 * (the commentary ticker) at the moment the mechanic they teach first becomes a live decision —
 * never as an upfront tutorial.
 */
object OnboardingHints {
    private var prefs: PlayerPreferences? = null
    private var bankingHintShown = false

    /** Seed from persistence. Call once at startup (alongside [Achievements.init]). */
    fun init(playerPreferences: PlayerPreferences) {
        prefs = playerPreferences
        bankingHintShown = playerPreferences.getBankingHintShown()
    }

    /** True exactly once — the first time the banking decision goes live for this player. */
    fun consumeBankingHint(): Boolean {
        if (bankingHintShown) return false
        bankingHintShown = true
        prefs?.setBankingHintShown(true)
        return true
    }
}

/** Records the Lucky achievement with the OS games service, where one is configured. */
internal expect fun reportLuckyAchievementToPlatform()

/** Reads the Lucky achievement back from the OS games service so it survives reinstalls / new devices. */
internal expect fun reconcileLuckyAchievementFromPlatform()
